import { CheckCircle2, Sun, Target, Zap, Shield, Focus, TrendingUp, Activity, Calendar, Moon } from "lucide-react";
import { useEffect } from "react";

const DAILY_GOALS = [
    { text: "Despertar-se d'hora (9:00)", icon: Sun },
    { text: "Dutxar + Rentar dents", icon: Activity },
    { text: "Estudiar 3h", icon: Focus },
    { text: "Llegir 30min", icon: Target },
    { text: "Escriure 30min", icon: Zap },
    { text: "Anar a domir d'hora (<1:00)", icon: Moon }
];

const OTHER_GOALS = [
    { text: "Treure notes +8 uni", icon: TrendingUp },
    { text: "Projecte Finances", icon: Target }
];

const PRINCIPLES = [
    {
        title: "La motivació inicial s'acaba; el sistema és el que et manté.",
        desc: "La majoria falla no per mandra, sinó perquè ningú ensenya com estructurar el dia.",
        icon: Shield
    },
    {
        title: "Comença el dia offline.",
        desc: "Evita el mòbil i les xarxes els primers 20–30 minuts per guanyar claredat i no viure en mode reacció.",
        icon: Sun
    },
    {
        title: "Una sola prioritat real al dia.",
        desc: "No cal fer moltes coses: n'hi ha prou amb una que importi de veritat i fer-la bé.",
        icon: Target
    },
    {
        title: "Fes primer el que costa.",
        desc: "La tasca que evites és la que et desbloqueja el dia; si la deixes per després, t'arrossega mentalment.",
        icon: Zap
    },
    {
        title: "Limita els inputs.",
        desc: "Estar informat no és el mateix que estar enfocat; el doomscrolling no suma res si no tens un objectiu clar.",
        icon: Focus
    },
    {
        title: "Produeix alguna cosa cada dia.",
        desc: "Crear (un post, codi, disseny, document…) és el que fa que el progrés composi; consumir no.",
        icon: TrendingUp
    },
    {
        title: "Cuida el cos per rendir millor.",
        desc: "Mou-te, camina, entrena: una ment clara necessita un cos funcionant.",
        icon: Activity
    },
    {
        title: "Revisa el dia.",
        desc: "Pregunta't què has fet bé, què has malgastat i què milloraràs demà. És feedback, no autoflagel·lació.",
        icon: Calendar
    },
    {
        title: "Dorm prou.",
        desc: "El descans és part del sistema; sense son no hi ha focus ni constància.",
        icon: Moon
    },
    {
        title: "\"Lock in\" és la clau.",
        desc: "No és fer-ho tot, sinó fer les coses correctes cada dia, aguantar la part avorrida i ser consistent. La \"sort\" ve després.",
        icon: Zap
    }
];

export function Objectius() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    
    return (
        <div className="min-h-screen bg-black text-white p-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-12">
                    <h1 className="text-5xl font-bold bg-linear-to-r from-white to-gray-400 bg-clip-text text-transparent mb-2">
                        Objectius
                    </h1>
                    <p className="text-gray-500 text-sm italic">
                        "Lock in" no és fer-ho tot, sinó fer les coses correctes cada dia
                    </p>
                </div>

                {/* Goals Grid */}
                <div className="grid md:grid-cols-2 gap-6 mb-12">
                    {/* Daily Goals */}
                    <div className="p-6 rounded-xl bg-linear-to-br from-blue-600/10 to-blue-700/5 border border-blue-500/20 hover:border-blue-500/40 transition-all">
                        <div className="flex items-center gap-2 mb-6">
                            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                                <Calendar className="w-5 h-5 text-blue-400" />
                            </div>
                            <h2 className="text-2xl font-bold text-blue-400">Cada dia</h2>
                        </div>
                        <div className="space-y-3">
                            {DAILY_GOALS.map((goal, index) => {
                                const Icon = goal.icon;
                                return (
                                    <div key={index} className="flex items-center gap-3 group hover:bg-white/5 p-3 rounded-lg transition-all">
                                        <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-all">
                                            <Icon className="w-4 h-4 text-blue-400 group-hover:scale-110 transition-transform" />
                                        </div>
                                        <span className="text-gray-200 text-sm">{goal.text}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Other Goals */}
                    <div className="p-6 rounded-xl bg-linear-to-br from-green-600/10 to-green-700/5 border border-green-500/20 hover:border-green-500/40 transition-all">
                        <div className="flex items-center gap-2 mb-6">
                            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                                <Target className="w-5 h-5 text-green-400" />
                            </div>
                            <h2 className="text-2xl font-bold text-green-400">Altres</h2>
                        </div>
                        <div className="space-y-3">
                            {OTHER_GOALS.map((goal, index) => {
                                const Icon = goal.icon;
                                return (
                                    <div key={index} className="flex items-center gap-3 group hover:bg-white/5 p-3 rounded-lg transition-all">
                                        <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center group-hover:bg-green-500/20 transition-all">
                                            <Icon className="w-4 h-4 text-green-400 group-hover:scale-110 transition-transform" />
                                        </div>
                                        <span className="text-gray-200 text-sm">{goal.text}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <div className="border-t border-white/10 my-12"></div>

                {/* Principles */}
                <div className="mb-12">
                    <h2 className="text-3xl font-bold mb-8 text-center bg-linear-to-r from-white to-gray-500 bg-clip-text text-transparent">
                        Principis
                    </h2>
                    <div className="grid md:grid-cols-2 gap-3">
                        {PRINCIPLES.map((principle, index) => {
                            const Icon = principle.icon;
                            return (
                                <div
                                    key={index}
                                    className="group p-4 rounded-lg bg-linear-to-br from-white/8 to-white/2 border border-white/15 hover:border-white/35 hover:from-white/12 transition-all duration-200"
                                >
                                    <div className="flex items-start gap-3 mb-2">
                                        <div className="w-9 h-9 rounded-lg bg-linear-to-br from-purple-500/25 to-purple-600/10 flex items-center justify-center group-hover:from-purple-500/35 transition-all shrink-0">
                                            <Icon className="w-5 h-5 text-purple-300" />
                                        </div>
                                        <h3 className="font-bold text-white text-sm leading-snug">
                                            {principle.title}
                                        </h3>
                                    </div>
                                    <p className="text-gray-400 text-xs leading-relaxed ml-12">
                                        {principle.desc}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
